import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Form, Alert, Spinner, InputGroup, Collapse } from 'react-bootstrap';
import { FiEye, FiEyeOff, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import useAuthStore from '../../stores/authStore';
import { getAndClearReturnUrl, getValidReturnUrlOrDefault } from '../../utils/returnUrl';
import { isMicrosoftLoginButtonVisible } from '../../auth/msalService';

function formatAttemptsRemaining(attemptsRemaining) {
  if (attemptsRemaining === 0) {
    return 'Account tijdelijk geblokkeerd. Probeer het later opnieuw.';
  }
  if (attemptsRemaining === 1) {
    return 'Nog 1 poging over voordat het account wordt geblokkeerd.';
  }
  return `Nog ${attemptsRemaining} pogingen over voordat het account wordt geblokkeerd.`;
}

function MicrosoftLogo() {
  return (
    <svg
      className="login-ms-button__logo"
      xmlns="http://www.w3.org/2000/svg"
      width="21"
      height="21"
      viewBox="0 0 21 21"
      aria-hidden="true"
    >
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

/**
 * Inlogformulier: Microsoft primair, gebruikersnaam/wachtwoord ingeklapt (beheerders).
 */
function LoginForm({ initialError = '' }) {
  const showMicrosoft = isMicrosoftLoginButtonVisible();
  const [legacyOpen, setLegacyOpen] = useState(!showMicrosoft);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(initialError);
  const [attemptsRemaining, setAttemptsRemaining] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login, loginWithMicrosoft } = useAuthStore();
  const [msLoading, setMsLoading] = useState(false);

  const busy = loading || msLoading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setAttemptsRemaining(null);
    setLoading(true);

    const result = await login(username, password);

    if (result.success) {
      const returnUrl = getAndClearReturnUrl();
      const target = getValidReturnUrlOrDefault(returnUrl);
      navigate(target || '/projects');
    } else {
      setError(result.error || 'Inloggen mislukt');
      setAttemptsRemaining(result.attemptsRemaining);
      setLoading(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    setError('');
    setAttemptsRemaining(null);
    setMsLoading(true);
    const result = await loginWithMicrosoft();
    if (!result.success) {
      setError(result.error || 'Microsoft-inloggen mislukt');
      setMsLoading(false);
    }
  };

  const toggleLegacy = () => {
    setLegacyOpen((open) => !open);
  };

  return (
    <div className="login-page-container">
      <div className="login-card">
        <h3 className="login-title">Inloggen</h3>

        {error && (
          <Alert variant="danger" className="login-alert">
            {error}
            {attemptsRemaining !== null && attemptsRemaining !== undefined && (
              <div className="mt-2 mb-0">
                <small>{formatAttemptsRemaining(attemptsRemaining)}</small>
              </div>
            )}
          </Alert>
        )}

        {showMicrosoft && (
          <div className="login-ms-primary">
            <button
              type="button"
              className="login-ms-button"
              disabled={busy}
              onClick={handleMicrosoftLogin}
              aria-label="Doorgaan met Microsoft"
            >
              {msLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="login-ms-button__spinner" />
                  <span>Even geduld, u wordt doorverbonden…</span>
                </>
              ) : (
                <>
                  <MicrosoftLogo />
                  <span>Doorgaan met Microsoft</span>
                </>
              )}
            </button>
            <p className="login-ms-hint">
              U wordt doorverbonden met een beveiligde Microsoft-inlogpagina.
              Zorg dat u verbonden bent met het bedrijfsnetwerk (VPN) voordat u inlogt.
            </p>
          </div>
        )}

        {showMicrosoft && (
          <div className="login-legacy-section">
            <button
              type="button"
              className="login-legacy-toggle"
              onClick={toggleLegacy}
              aria-expanded={legacyOpen}
              aria-controls="login-legacy-panel"
              disabled={busy}
            >
              <span>Inloggen met gebruikersnaam (beheerders)</span>
              {legacyOpen ? (
                <FiChevronUp size={18} aria-hidden="true" />
              ) : (
                <FiChevronDown size={18} aria-hidden="true" />
              )}
            </button>

            <Collapse in={legacyOpen}>
              <div id="login-legacy-panel" className="login-legacy-panel">
                <Form onSubmit={handleSubmit} className="login-form">
                  <Form.Group className="mb-3">
                    <Form.Label>Gebruikersnaam</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Voer gebruikersnaam in"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                      disabled={busy}
                      autoComplete="username"
                    />
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label>Wachtwoord</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Voer wachtwoord in"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        disabled={busy}
                        autoComplete="current-password"
                        aria-describedby="password-toggle"
                      />
                      <InputGroup.Text
                        id="password-toggle"
                        as="button"
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') e.preventDefault();
                        }}
                        disabled={busy}
                        className="password-toggle-btn"
                        aria-label={showPassword ? 'Verberg wachtwoord' : 'Toon wachtwoord'}
                        tabIndex={0}
                      >
                        {showPassword ? (
                          <FiEyeOff size={18} aria-hidden="true" />
                        ) : (
                          <FiEye size={18} aria-hidden="true" />
                        )}
                      </InputGroup.Text>
                    </InputGroup>
                  </Form.Group>

                  <Button
                    type="submit"
                    variant="secondary"
                    className="login-legacy-submit"
                    disabled={busy}
                  >
                    {loading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Bezig met inloggen…
                      </>
                    ) : (
                      'Inloggen'
                    )}
                  </Button>
                </Form>
              </div>
            </Collapse>
          </div>
        )}

        {!showMicrosoft && (
          <Form onSubmit={handleSubmit} className="login-form">
            <Form.Group className="mb-3">
              <Form.Label>Gebruikersnaam</Form.Label>
              <Form.Control
                type="text"
                placeholder="Voer gebruikersnaam in"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                disabled={busy}
                autoComplete="username"
              />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label>Wachtwoord</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Voer wachtwoord in"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={busy}
                  autoComplete="current-password"
                  aria-describedby="password-toggle-standalone"
                />
                <InputGroup.Text
                  id="password-toggle-standalone"
                  as="button"
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  disabled={busy}
                  className="password-toggle-btn"
                  aria-label={showPassword ? 'Verberg wachtwoord' : 'Toon wachtwoord'}
                  tabIndex={0}
                >
                  {showPassword ? (
                    <FiEyeOff size={18} aria-hidden="true" />
                  ) : (
                    <FiEye size={18} aria-hidden="true" />
                  )}
                </InputGroup.Text>
              </InputGroup>
            </Form.Group>

            <Button type="submit" className="login-button" disabled={busy}>
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Bezig met inloggen…
                </>
              ) : (
                'Inloggen'
              )}
            </Button>
          </Form>
        )}
      </div>
    </div>
  );
}

export default LoginForm;
